import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { maternityService } from '../services/maternityService'
import { MaternityVisitFormData, MaternityVisit } from '../types/maternity'
import { calculateGestationalAge } from '../utils/dueDateCalculator'
import { validateWeightInput } from '../utils/vitalMeasurementsValidation'
import './MaternityVisitForm.css'

interface MaternityVisitFormProps {
  patientId: string
  pregnancyId: string
  visit?: MaternityVisit
  onSuccess: () => void
  onCancel: () => void
}

export function MaternityVisitForm({
  patientId,
  pregnancyId,
  visit,
  onSuccess,
  onCancel,
}: MaternityVisitFormProps) {
  const queryClient = useQueryClient()
  const [visitDate, setVisitDate] = useState(visit?.visitDate || new Date().toISOString().split('T')[0])
  const [visitType, setVisitType] = useState<'antenatal' | 'postnatal'>(visit?.visitType || 'antenatal')
  const consultationId = visit?.consultationId || ''
  const [bloodPressure, setBloodPressure] = useState<{ systolic: number; diastolic: number } | undefined>(
    visit?.bloodPressure
  )
  const [weight, setWeight] = useState<number | undefined>(visit?.weight)
  const [weightError, setWeightError] = useState<string | null>(null)
  const [fundalHeight, setFundalHeight] = useState<number | undefined>(visit?.fundalHeight)
  const [fetalHeartRate, setFetalHeartRate] = useState<number | undefined>(visit?.fetalHeartRate)
  const [urineTest, setUrineTest] = useState(visit?.urineTest)
  const [presentation, setPresentation] = useState(visit?.presentation)
  const [cervicalDilation, setCervicalDilation] = useState<number | undefined>(visit?.cervicalDilation)
  const [maternalMood, setMaternalMood] = useState(visit?.maternalMood || '')
  const [notes, setNotes] = useState(visit?.notes || '')
  const [gestationalAge, setGestationalAge] = useState<string>(visit?.gestationalAge || '')

  // Get pregnancy to calculate gestational age
  const { data: pregnancy, isLoading: isLoadingPregnancy } = useQuery({
    queryKey: ['pregnancy', pregnancyId],
    queryFn: () => maternityService.getPregnancy(pregnancyId, patientId),
    enabled: !!pregnancyId && !!patientId,
  })

  useEffect(() => {
    if (pregnancy?.lastMenstrualPeriod && visitDate) {
      try {
        // Parse visitDate properly - it's in YYYY-MM-DD format
        const visitDateObj = new Date(visitDate + 'T00:00:00') // Add time to avoid timezone issues
        const lmpDate = new Date(pregnancy.lastMenstrualPeriod + 'T00:00:00')
        
        // Only calculate if dates are valid
        if (!isNaN(visitDateObj.getTime()) && !isNaN(lmpDate.getTime())) {
          const ga = calculateGestationalAge(pregnancy.lastMenstrualPeriod, visitDateObj)
          if (ga && ga.display) {
            setGestationalAge(ga.display)
          } else {
            setGestationalAge('')
          }
        } else {
          setGestationalAge('')
        }
      } catch (error) {
        console.error('Error calculating gestational age:', error)
        setGestationalAge('')
      }
    } else {
      setGestationalAge('')
    }
  }, [pregnancy, visitDate])

  const createMutation = useMutation({
    mutationFn: (data: MaternityVisitFormData) => maternityService.createVisit(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maternity-visits', pregnancyId] })
      queryClient.invalidateQueries({ queryKey: ['pregnancies', patientId] })
      onSuccess()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<MaternityVisitFormData>) =>
      visit
        ? maternityService.updateVisit(visit.visitId, pregnancyId, data)
        : Promise.reject(new Error('Visit not found')),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maternity-visits', pregnancyId] })
      queryClient.invalidateQueries({ queryKey: ['maternity-visit', visit?.visitId] })
      onSuccess()
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setWeightError(null)

    if (weight !== undefined && weight !== null) {
      const wErr = validateWeightInput(weight, 'kg')
      if (wErr) {
        setWeightError(wErr)
        return
      }
    }

    // Ensure gestational age is calculated if not already set
    let finalGestationalAge = gestationalAge
    if (!finalGestationalAge && pregnancy?.lastMenstrualPeriod && visitDate) {
      try {
        const visitDateObj = new Date(visitDate + 'T00:00:00')
        const ga = calculateGestationalAge(pregnancy.lastMenstrualPeriod, visitDateObj)
        finalGestationalAge = ga.display
      } catch (error) {
        console.error('Error calculating gestational age on submit:', error)
      }
    }

    const formData: MaternityVisitFormData = {
      pregnancyId,
      patientId,
      visitDate,
      visitType,
      gestationalAge: finalGestationalAge || '', // Include calculated gestational age
      consultationId: consultationId || undefined,
      bloodPressure,
      weight,
      fundalHeight,
      fetalHeartRate,
      urineTest,
      presentation,
      cervicalDilation,
      maternalMood: maternalMood || undefined,
      notes: notes || undefined,
    }

    try {
      if (visit?.visitId) {
        // For updates, send all fields (not just partial) to ensure complete update
        console.log('Updating visit:', visit.visitId, 'with data:', formData)
        await updateMutation.mutateAsync(formData)
      } else {
        console.log('Creating new visit with data:', formData)
        await createMutation.mutateAsync(formData)
      }
    } catch (error) {
      console.error('Error saving visit:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to save visit. Please try again.'
      alert(errorMessage)
    }
  }

  return (
    <form className="maternity-visit-form" onSubmit={handleSubmit}>
      <div className="form-section">
        <h3>Visit Information</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="visitDate">
              Visit Date <span className="required">*</span>
            </label>
            <input
              id="visitDate"
              type="date"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value)}
              required
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
          <div className="form-group">
            <label htmlFor="visitType">
              Visit Type <span className="required">*</span>
            </label>
            <select
              id="visitType"
              value={visitType}
              onChange={(e) => setVisitType(e.target.value as 'antenatal' | 'postnatal')}
              required
            >
              <option value="antenatal">Antenatal</option>
              <option value="postnatal">Postnatal</option>
            </select>
          </div>
        </div>
        {(gestationalAge || isLoadingPregnancy) && (
          <div className="form-group" style={{ marginTop: '0.75rem' }}>
            <label>Gestational Age</label>
            <div className="readonly-field">
              {isLoadingPregnancy ? 'Calculating...' : gestationalAge || 'Unable to calculate'}
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Vital Signs & Measurements</h3>
        <div className="form-group">
          <label>Blood Pressure</label>
          <div className="bp-group">
            <div>
              <label htmlFor="systolic" style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>
                Systolic
              </label>
              <input
                id="systolic"
                type="number"
                min="0"
                max="300"
                value={bloodPressure?.systolic || ''}
                onChange={(e) =>
                  setBloodPressure({
                    systolic: parseInt(e.target.value) || 0,
                    diastolic: bloodPressure?.diastolic || 0,
                  })
                }
                placeholder="120"
              />
            </div>
            <span className="bp-separator">/</span>
            <div>
              <label htmlFor="diastolic" style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>
                Diastolic
              </label>
              <input
                id="diastolic"
                type="number"
                min="0"
                max="200"
                value={bloodPressure?.diastolic || ''}
                onChange={(e) =>
                  setBloodPressure({
                    systolic: bloodPressure?.systolic || 0,
                    diastolic: parseInt(e.target.value) || 0,
                  })
                }
                placeholder="80"
              />
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="weight">Weight (kg)</label>
            <input
              id="weight"
              type="number"
              step="0.1"
              min="0"
              value={weight || ''}
              onChange={(e) => {
                setWeightError(null)
                setWeight(e.target.value ? parseFloat(e.target.value) : undefined)
              }}
              aria-invalid={weightError ? true : undefined}
              aria-describedby={weightError ? 'weight-error' : undefined}
            />
            {weightError && (
              <span id="weight-error" className="error-message" role="alert">
                {weightError}
              </span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="fundalHeight">Fundal Height (cm)</label>
            <input
              id="fundalHeight"
              type="number"
              step="0.1"
              min="0"
              value={fundalHeight || ''}
              onChange={(e) => setFundalHeight(e.target.value ? parseFloat(e.target.value) : undefined)}
            />
          </div>
        </div>
        {visitType === 'antenatal' && (
          <div className="form-group">
            <label htmlFor="fetalHeartRate">Fetal Heart Rate (bpm)</label>
            <input
              id="fetalHeartRate"
              type="number"
              min="0"
              max="300"
              value={fetalHeartRate || ''}
              onChange={(e) => setFetalHeartRate(e.target.value ? parseInt(e.target.value) : undefined)}
            />
          </div>
        )}
      </div>

      {visitType === 'antenatal' && (
        <div className="form-section">
          <h3>Urine Test</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="urineProtein">Protein</label>
              <select
                id="urineProtein"
                value={urineTest?.protein || ''}
                onChange={(e) =>
                  setUrineTest({
                    ...urineTest,
                    protein: e.target.value as any,
                  })
                }
              >
                <option value="">Not tested</option>
                <option value="negative">Negative</option>
                <option value="trace">Trace</option>
                <option value="1+">1+</option>
                <option value="2+">2+</option>
                <option value="3+">3+</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="urineGlucose">Glucose</label>
              <select
                id="urineGlucose"
                value={urineTest?.glucose || ''}
                onChange={(e) =>
                  setUrineTest({
                    ...urineTest,
                    glucose: e.target.value as any,
                  })
                }
              >
                <option value="">Not tested</option>
                <option value="negative">Negative</option>
                <option value="trace">Trace</option>
                <option value="1+">1+</option>
                <option value="2+">2+</option>
                <option value="3+">3+</option>
              </select>
            </div>
          </div>
          <div className="checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={urineTest?.nitrites || false}
                onChange={(e) =>
                  setUrineTest({
                    ...urineTest,
                    nitrites: e.target.checked,
                  })
                }
              />
              Nitrites
            </label>
            <label>
              <input
                type="checkbox"
                checked={urineTest?.leukocytes || false}
                onChange={(e) =>
                  setUrineTest({
                    ...urineTest,
                    leukocytes: e.target.checked,
                  })
                }
              />
              Leukocytes
            </label>
          </div>
        </div>
      )}

      {visitType === 'antenatal' && (
        <div className="form-section">
          <h3>Examination</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="presentation">Presentation</label>
              <select
                id="presentation"
                value={presentation || ''}
                onChange={(e) => setPresentation(e.target.value as any)}
              >
                <option value="">Not examined</option>
                <option value="cephalic">Cephalic</option>
                <option value="breech">Breech</option>
                <option value="transverse">Transverse</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="cervicalDilation">Cervical Dilation (cm)</label>
              <input
                id="cervicalDilation"
                type="number"
                step="0.5"
                min="0"
                max="10"
                value={cervicalDilation || ''}
                onChange={(e) => setCervicalDilation(e.target.value ? parseFloat(e.target.value) : undefined)}
              />
            </div>
          </div>
        </div>
      )}

      <div className="form-section">
        <h3>Assessment</h3>
        <div className="form-group">
          <label htmlFor="maternalMood">Maternal Mood</label>
          <textarea
            id="maternalMood"
            value={maternalMood}
            onChange={(e) => setMaternalMood(e.target.value)}
            rows={3}
            placeholder="Assessment of maternal mood and wellbeing..."
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Notes</h3>
        <div className="form-group">
          <label htmlFor="notes">Clinical Notes</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={5}
            placeholder="Additional clinical notes..."
          />
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={createMutation.isPending || updateMutation.isPending}
        >
          {createMutation.isPending || updateMutation.isPending
            ? 'Saving...'
            : visit
              ? 'Update Visit'
              : 'Create Visit'}
        </button>
      </div>
    </form>
  )
}
